import { Switch } from '@react-spectrum/s2';
import { useCallback, useEffect, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { ProcessStatusBadge } from '@/spaces/campaigns/ProcessStatusBadge';

import type { PanelProcess } from '../../../../../bridge/types/state.ts';

interface UseProcessActiveSwitchArgs {
	processId: string;
	status: PanelProcess['status'];
	updatedAt?: string;
	lastActionFailed?: PanelProcess['last_action_failed'];
	pauseAriaLabel?: string;
	resumeAriaLabel?: string;
}

export const useProcessActiveSwitch = ({
	processId,
	status,
	updatedAt,
	lastActionFailed,
	pauseAriaLabel = 'Pause process',
	resumeAriaLabel = 'Resume process',
}: UseProcessActiveSwitchArgs) => {
	const bridgeApi = useBridgeApi();
	const isActive = status === 'active';
	const isPaused = status === 'paused';

	const [pausePending, setPausePending] = useState(false);
	// Dismissed locally so trying again clears the message immediately, rather than
	// leaving a stale warning up while the retry is in flight.
	const [dismissedFailure, setDismissedFailure] = useState(false);
	useEffect(() => {
		setPausePending(false);
	}, [status, updatedAt]);

	const handlePause = useCallback(() => {
		setPausePending(true);
		setDismissedFailure(true);
		bridgeApi.sendAction({
			type: 'do_pause_process',
			process_id: processId,
		});
	}, [bridgeApi, processId]);

	const handleResume = useCallback(() => {
		setPausePending(true);
		setDismissedFailure(true);
		bridgeApi.sendAction({
			type: 'do_resume_process',
			process_id: processId,
		});
	}, [bridgeApi, processId]);

	useEffect(() => {
		if (lastActionFailed) {
			setDismissedFailure(false);
		}
	}, [lastActionFailed, updatedAt]);

	const headerTagRender = () => {
		const isActionable = isActive || isPaused;
		if (!isActionable) {
			return <ProcessStatusBadge status={status} />;
		}
		const showAsActive = isActive ? !pausePending : pausePending;
		return (
			<Switch
				isSelected={showAsActive}
				onChange={showAsActive ? handlePause : handleResume}
				isDisabled={pausePending}
				isEmphasized
				size="M"
				aria-label={showAsActive ? pauseAriaLabel : resumeAriaLabel}
			>
				{showAsActive ? 'Active' : 'Paused'}
			</Switch>
		);
	};

	// Returned separately from headerTagRender, which must keep rendering exactly one
	// Switch — panels place this where their own layout allows.
	const actionFailureRender = () => {
		if (!lastActionFailed || dismissedFailure) {
			return null;
		}
		const verb = lastActionFailed === 'pause' ? 'pause' : 'resume';
		return (
			<div
				role="alert"
				className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm
					text-red-700"
			>
				{`Couldn't ${verb} this job just now — it's still ${
					lastActionFailed === 'pause' ? 'running' : 'paused'
				}. Try again in a moment.`}
			</div>
		);
	};

	// Suspending the job behind the scenes is queued, not instant, so something
	// already on its way out can still land after the switch reads Paused. Said once,
	// on the confirmed state only — saying it optimistically would undercut a pause
	// that did work.
	const pausedCaveatRender = () => {
		if (!isPaused || pausePending) {
			return null;
		}
		return (
			<p className="text-sm text-[var(--text-muted)]">
				Paused. Anything already sending may still go out.
			</p>
		);
	};

	return { headerTagRender, actionFailureRender, pausedCaveatRender };
};
